<template>
  <div class="full-page-width relative locations">
    <div class="row">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :title="$t('webhooks')"
          :text="$t('webhooks-info-text')"
          :btnText="$t('add_webhook')"
          @action="openNewPopup" />
        <!-- InfoTextBox's own button floats left and is not cleared internally, so the buttons
             below need an explicit clearfix or they render on top of it. They're stacked (not
             floated) because the column is too narrow to fit them side by side. -->
        <div class="clearfix"></div>
        <Btn
          @click.native="downloadDocumentation('md')"
          class="light bordered download-btn m-t-20"
          :icon="{ name: 'cloudDownload', size: '18' }"
          :btnText="$t('download_api_documentation')" />
        <Btn
          @click.native="downloadDocumentation('html')"
          class="light bordered download-btn m-t-15"
          :icon="{ name: 'document', size: '18' }"
          :btnText="$t('download_as_html')" />
      </div>
      <div class="col-xs-8">
        <ul class="list-table">
          <li
            class="list-table-item"
            @click="goToWebhook(item.id)"
            v-for="(item, index) in get_webhooks" :key="`webhook-${index}`">
            <div class="table-cell small">
              <div class="list-icon-block">
                <div class="status-indicator" :class="item.active ? 'active' : 'expired'"></div>
                <Icon :circle=true name="network" size="20" />
              </div>
            </div>
            <div class="table-cell">
              <p>
                <span class="main-info">{{ item.name || item.url }}</span>
                <span class="additional-info">{{ item.event }}</span>
              </p>
            </div>
            <div class="table-cell">
              <p>
                <span class="additional-info">{{ item.url }}</span>
              </p>
            </div>
          </li>
        </ul>
        <p class="c-light m-t-20" v-if="!get_webhooks.length">{{ $t('no_webhooks_registered') }}</p>
      </div>
    </div>
    <Popup
      v-if="openPopup"
      :popupSmall=true
      min-height="300px"
      @cancel="togglePopup"
      @confirm="submitForm">
      <template v-slot:header>
        <h2>{{ $t('add_webhook') }}</h2>
      </template>
      <div>
        <div class="row">
          <div class="col-xs-12 m-b-20">
            <InputSelect
              :label="$t('event')"
              v-model="form.event"
              :rules="{required: true}"
              :shouldTranslate="false"
              :options="eventOptions" />
            <small class="field-hint" v-if="selectedEventDescription">{{ selectedEventDescription }}</small>
          </div>
          <div class="col-xs-12 m-b-20">
            <InputText
              :label="$t('name')"
              :placeholder="$t('webhook_name_placeholder')"
              v-model="form.name" />
          </div>
          <div class="col-xs-12 m-b-20">
            <InputText
              :label="$t('endpoint_url')"
              placeholder="https://"
              :rules="{required: true}"
              v-model="form.url" />
            <small class="field-hint">{{ $t('webhook_url_hint') }}</small>
          </div>
        </div>
      </div>
    </Popup>
  </div>
</template>
<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import InputSelect from '@/components/elements/inputs/select.vue';
import Popup from '@/components/global/popup';
import InfoTextBox from '@/components/global/infoTextBox';

export default {
  name: 'Webhooks',
  components: { Btn, Popup, InputText, InputSelect, InfoTextBox },
  data() {
    return {
      openPopup: false,
      form: {
        event: '',
        name: '',
        url: '',
        active: true,
        config_json: { statuses: [] },
      },
    };
  },
  computed: {
    ...mapGetters([
      'get_webhooks',
      'get_webhook_events',
      'get_webhook_event_definition',
    ]),
    // Event names are API identifiers (shipment.status), never translated.
    eventOptions() {
      return this.get_webhook_events.events.map((definition) => ({
        text: definition.event,
        value: definition.event,
      }));
    },
    selectedEventDescription() {
      const definition = this.get_webhook_event_definition(this.form.event);

      return definition ? definition.description : '';
    },
  },
  created() {
    this.$store.dispatch('GET_WEBHOOK_EVENTS');
    this.$store.dispatch('GET_WEBHOOKS');
  },
  methods: {
    clearLocalFormData() {
      this.form = {
        event: '',
        name: '',
        url: '',
        active: true,
        config_json: { statuses: [] },
      };
    },
    togglePopup() {
      this.openPopup = !this.openPopup;
      this.clearLocalFormData();
    },
    openNewPopup() {
      this.togglePopup();
    },
    submitForm() {
      if (!this.form.event || !this.form.url) return;

      this.$store.dispatch('SET_WEBHOOK', { form: this.form }).then((webhook) => {
        this.openPopup = false;
        this.clearLocalFormData();
        this.$store.dispatch('GET_WEBHOOKS');
        // Straight into the detail page — that is where the signing secret is shown, which is
        // the first thing the client needs after registering.
        if (webhook && webhook.id) {
          this.goToWebhook(webhook.id);
        }
      }).catch(() => {});
    },
    goToWebhook(id) {
      this.$router.push({ path: `/settings/integration/webhooks/${id}` });
    },
    downloadDocumentation(format) {
      this.$store.dispatch('DOWNLOAD_WEBHOOK_DOCUMENTATION', { format });
    },
  },
};
</script>
<style scoped>
/* .bordered (button.scss) sets padding: 12px 28px, overwriting the base .button rule's
   padding-left: 40px that normally reserves room for the absolutely-positioned .btn-icon
   (left: 15px). Every other bordered+icon button in the app is icon-only (fix-40, no text), so
   this never surfaced before: with text present, the icon renders on top of the first letters.
   Restore the icon clearance for just these two buttons rather than touching the shared component. */
.download-btn.bordered {
  padding-left: 40px;
}

/* Block-level so the two buttons stack one per line, but shrink-wrapped to their text instead of
   stretching to the column width like display: block would. */
.download-btn {
  display: table;
}

/* .c-light (used elsewhere in the app, e.g. addTemplate.vue) isn't defined anywhere in the SCSS —
   it's a no-op, so hint text under a field fell back to the ambient (dark, input-sized) text with
   no gap above it. Defined here instead of touching the shared stylesheet, since .c-light's exact
   fallback behavior is relied on unchanged elsewhere. font-size/color match the existing "light"
   text used in InfoTextBox (info-text-box.scss: color: $color-light, i.e. #989fa6). */
.field-hint {
  display: block;
  margin-top: 8px;
  color: #989fa6;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
}
</style>
